import { Editor, type OnMount } from "@monaco-editor/react";
import { Box, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { KeyCode, KeyMod } from "monaco-editor";
import * as React from "react";

import { RobotoButton } from "../RobotoButton";
import { RobotoTypography } from "../RobotoTypography";

interface JsonMetadataEditorProps extends React.PropsWithChildren {
  value: unknown;
  onObjectValueChanged?: (newValue: object) => void;
}

export function JsonMetadataEditor({
  value,
  onObjectValueChanged,
  children,
}: JsonMetadataEditorProps) {
  const [open, setOpen] = React.useState(false);
  const [editText, setEditText] = React.useState(
    JSON.stringify(value, null, 2),
  );
  const [editError, setEditError] = React.useState<string | null>(null);

  const handleOpen = () => {
    setEditText(JSON.stringify(value, null, 2));
    setEditError(null);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleSave = () => {
    try {
      const parsedValue = JSON.parse(editText) as object;
      if (
        onObjectValueChanged &&
        JSON.stringify(parsedValue) !== JSON.stringify(value)
      ) {
        onObjectValueChanged(parsedValue);
      }
      handleClose();
    } catch (e) {
      setEditError(`Invalid JSON: ${e as Error}`);
    }
  };

  const handleSaveRef = React.useRef(handleSave);
  handleSaveRef.current = handleSave;

  const onEditorMount: OnMount = (editor) => {
    editor.addCommand(KeyMod.CtrlCmd | KeyCode.Enter, () => {
      handleSaveRef.current();
    });
  };

  const theme = useTheme();
  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Edit Metadata as JSON</DialogTitle>
        <DialogContent>
          <Editor
            height={"40vh"}
            language={"json"}
            theme={theme.palette.mode === "dark" ? "vs-dark" : "vs-light"}
            value={editText}
            onChange={(newValue) => {
              setEditText(newValue ?? "");
              try {
                JSON.parse(newValue ?? "");
                setEditError(null);
              } catch (e) {
                setEditError(`Invalid JSON: ${e as Error}`);
              }
            }}
            onMount={onEditorMount}
            options={{
              showUnused: false,
              folding: true,
              automaticLayout: true,
              // Another option is "interval", which only shows your current line.
              lineNumbers: "on",
              scrollBeyondLastLine: false,
              readOnly: false,

              // If unset, this defaults to 5, and pads line numbers, so they'll look uniform up to 5 digits of line number.
              // That uses a ton of horizontal space for basically a huge margin, so we want to disable it.
              lineNumbersMinChars: 2,

              // This stops Monaco from intercepting the right-click menu with its own command menu, and defaults to
              // whatever the browser was normally doing.
              contextmenu: false,

              // This gets rid of the thing that shows a really tiny version of your whole codebase
              minimap: {
                enabled: false,
              },

              // This gets rid of persistent scrollbars
              scrollbar: {
                horizontal: "hidden",
                vertical: "hidden",
                useShadows: false,
              },

              // This gets rid of an outline of where the scroll bar used to be
              overviewRulerBorder: false,

              // This gets rid of a little grey line above the outline
              overviewRulerLanes: 0,

              fontSize: theme.typography.fontSize,

              renderLineHighlight: "none",

              wordWrap: "on",
            }}
          />
          {editError && (
            <RobotoTypography color="error" variant="caption" sx={{ mt: 1 }}>
              {editError}
            </RobotoTypography>
          )}
        </DialogContent>
        <DialogActions>
          <RobotoButton
            onClick={handleClose}
            color="primary"
            eventName={"MetadataEditCanceled"}
          >
            Cancel
          </RobotoButton>
          <RobotoButton
            onClick={handleSave}
            color="primary"
            eventName={"MetadataEditSaved"}
          >
            Save
          </RobotoButton>
        </DialogActions>
      </Dialog>
      <Box
        onClick={onObjectValueChanged ? handleOpen : undefined}
        component="span"
        sx={{ cursor: onObjectValueChanged ? "pointer" : "default" }}
      >
        {children}
      </Box>
    </>
  );
}
